import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Venue } from '../venues';

const SITE = 'https://theoryapp.ru';

function upsertMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function VenueScreen({ venue }: { venue: Venue }) {
  useEffect(() => {
    document.documentElement.classList.add('landing-page');
    const prevTitle = document.title;
    document.title = venue.title;
    upsertMeta('description', venue.description);
    const url = `${SITE}${venue.path}`;
    upsertCanonical(url);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'venue-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': venue.slug === 'cifra' ? 'FoodEstablishment' : 'CafeOrCoffeeShop',
      name: venue.name,
      description: venue.description,
      url,
      address: {
        '@type': 'PostalAddress',
        streetAddress: venue.addressLines.join(' '),
        addressLocality: 'Долгопрудный',
        addressCountry: 'RU',
      },
    });
    document.getElementById('venue-jsonld')?.remove();
    document.head.appendChild(script);

    return () => {
      document.documentElement.classList.remove('landing-page');
      document.title = prevTitle;
      script.remove();
    };
  }, [venue]);

  return (
    <div className="landing-root min-h-screen text-white" style={{ background: '#0a0a0a' }}>
      <header className="flex items-center justify-between gap-4 px-5 sm:px-10 pt-6">
        <span
          className="text-[11px] tracking-[0.28em] uppercase"
          style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Manrope, sans-serif' }}
        >
          {venue.place}
        </span>
        <Link
          to="/"
          className="text-[12px] tracking-[0.18em] uppercase font-semibold whitespace-nowrap"
          style={{ fontFamily: 'Manrope, sans-serif', color: 'rgba(255,255,255,0.7)' }}
        >
          Ресторан
        </Link>
      </header>

      <main className="px-5 sm:px-10 pt-16 pb-20 max-w-3xl">
        <p
          className="text-[11px] tracking-[0.35em] uppercase mb-4"
          style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Manrope, sans-serif' }}
        >
          {venue.kind}
        </p>
        <h1
          className="text-5xl sm:text-7xl font-bold uppercase leading-[0.9] mb-8"
          style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.04em' }}
        >
          {venue.name}
        </h1>
        <p
          className="text-lg leading-relaxed mb-10 max-w-xl"
          style={{ color: 'rgba(255,255,255,0.72)', fontFamily: 'Manrope, sans-serif' }}
        >
          {venue.description}
        </p>
        <p
          className="text-[11px] tracking-[0.3em] uppercase mb-3"
          style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
        >
          Адрес
        </p>
        <p
          className="text-2xl font-bold leading-snug"
          style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}
        >
          {venue.addressLines.map((line) => (
            <span key={line}>
              {line}
              <br />
            </span>
          ))}
        </p>
      </main>
    </div>
  );
}
